import React, { PureComponent } from 'react'
import { withI18n, withI18nProps } from '@lingui/react'
import { connect } from 'react-redux'
import compose from 'lodash/fp/compose'

import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'

import {
  analyticsEvent,
  RECURRENT_SUBSCRIPTION_STOPPED,
} from 'client/shared/reducers/analytics-reducer'
import {
  deleteSubscription,
  chargeForRecurrent,
  loadProducts,
} from 'client/bookmate/reducers/subscription-reducer'

import RecurrentSubscriptionCard from 'client/bookmate/blocks/recurrent-subscription-card'

import { SubscriptionContext } from 'client/shared/helpers/subscription-helper'

import { RecurrentSubscription } from 'client/shared/types/subscription'
import { Dispatch, State } from 'shared/types/redux'
import { CancellationReason } from '../reducers/unsubscribe-reducer'
import { QueryParams } from 'shared/tools/url-helper'

type Props = {
  subscription: RecurrentSubscription
  login: string
  attributes: {
    params: {
      content_type: string
      slug: string
    }
  }
  cancellationReasons: {
    variants: CancellationReason[]
    loading: boolean
  }
  query?: QueryParams
  userId?: number
  showRetentionOffer?: () => void
  toggleCancellationOffer: () => void
} & { dispatch: Dispatch } & withI18nProps &
  ErrorBoundaryProps

class RecurrentSubscriptionCardBox extends PureComponent<Props> {
  static contextType = SubscriptionContext
  cancelRecurrent = async () => {
    const {
      subscription: { system, kind, terminate_on_cancel },
      i18n,
      dispatch,
    } = this.props

    if (system === 'telenor') {
      window.location = ('http://www.tln.rs/mt' as unknown) as Location
      return
    }

    let cancellationWarning = i18n.t`plan.cancel_recurrent_confirm`

    if (terminate_on_cancel) {
      cancellationWarning += ` ${i18n.t`subscription.cancellation_warning`}`
    }

    // eslint-disable-next-line no-alert
    if (confirm(cancellationWarning)) {
      await dispatch(deleteSubscription(system, kind))
      this.dispatchAnalyticsForCancelledRecurrent()
      dispatch(loadProducts())
    }
  }

  chargeForRecurrent = () => {
    const {
      subscription: { uuid: subscriptionId },
      dispatch,
    } = this.props

    if (subscriptionId) {
      dispatch(chargeForRecurrent(subscriptionId, this.context))
    }
  }

  dispatchAnalyticsForCancelledRecurrent() {
    const {
      dispatch,
      subscription: { system, kind, duration = '', uuid = '' },
    } = this.props

    const payload = {
      sub_type: kind,
      payment_method: system,
      duration,
      recurrent_uuid: uuid,
    }

    dispatch(analyticsEvent(RECURRENT_SUBSCRIPTION_STOPPED, payload))
  }

  render(): JSX.Element {
    const {
      cancellationReasons,
      subscription,
      login,
      query,
      userId,
      showRetentionOffer,
      toggleCancellationOffer,
      attributes,
    } = this.props

    return (
      <RecurrentSubscriptionCard
        userId={userId}
        cancellationReasons={cancellationReasons}
        subscription={subscription}
        cancelRecurrent={this.cancelRecurrent}
        chargeForRecurrent={this.chargeForRecurrent}
        login={login}
        query={query}
        showRetentionOffer={showRetentionOffer}
        toggleCancellationOffer={toggleCancellationOffer}
        libraryParams={attributes?.params}
      />
    )
  }
}

const connectWrapper = connect((state: State) => ({
  query: state.app.storedQuery,
  login: state.currentUser.data.login,
  userId: state.currentUser.data.id,
  cancellationReasons: state.unsubscribeProcess.cancellationReasons,
  attributes: state.showcase.rootNavigation.items[0],
}))

const wrappers = compose(
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(RecurrentSubscriptionCardBox)
